export type RaceAnswerOutcome = 'correct' | 'wrong' | 'timeout';

export type RaceRuleState = {
  score: number;
  streak: number;
  maxStreak: number;
  correct: number;
  wrong: number;
  timeouts: number;
  answered: number;
  totalResponseMs: number;
  turboCount: number;
};

export const initialRaceRuleState = (): RaceRuleState => ({
  score: 0,
  streak: 0,
  maxStreak: 0,
  correct: 0,
  wrong: 0,
  timeouts: 0,
  answered: 0,
  totalResponseMs: 0,
  turboCount: 0,
});

export function speedBonusForResponse(_responseMs: number, _timeLimitMs: number | null): number {
  const responseMs = Math.max(0, _responseMs);
  const referenceMs = Math.max(1_000, _timeLimitMs ?? 12_000);
  return Math.max(0, Math.min(40, Math.round(40 * (1 - responseMs / referenceMs))));
}

export function applyRaceAnswer(
  state: RaceRuleState,
  _answer: { outcome: RaceAnswerOutcome; responseMs: number; timeLimitMs: number | null },
) {
  const responseMs = Math.max(0, Math.trunc(_answer.responseMs));
  const isCorrect = _answer.outcome === 'correct';
  const nextStreak = isCorrect ? state.streak + 1 : 0;
  const base = isCorrect ? 100 : 0;
  const speed = isCorrect ? speedBonusForResponse(responseMs, _answer.timeLimitMs) : 0;
  const streak = isCorrect ? Math.min(40, nextStreak * 10) : 0;
  const penalty = _answer.outcome === 'wrong' ? -25 : _answer.outcome === 'timeout' ? -50 : 0;
  const rawDelta = base + speed + streak + penalty;
  const nextScore = Math.max(0, state.score + rawDelta);
  const scoreDelta = nextScore - state.score;
  const nextState: RaceRuleState = {
    score: nextScore,
    streak: nextStreak,
    maxStreak: Math.max(state.maxStreak, nextStreak),
    correct: state.correct + (isCorrect ? 1 : 0),
    wrong: state.wrong + (_answer.outcome === 'wrong' ? 1 : 0),
    timeouts: state.timeouts + (_answer.outcome === 'timeout' ? 1 : 0),
    answered: state.answered + 1,
    totalResponseMs: state.totalResponseMs + responseMs,
    turboCount: state.turboCount + (isCorrect ? 1 : 0),
  };
  return {
    state: nextState,
    breakdown: { base, speed, streak, penalty },
    scoreDelta,
  };
}

export function racePlacement(state: RaceRuleState, totalQuestions: number) {
  const safeTotal = Math.max(1, Math.trunc(totalQuestions));
  const progressRatio = Math.min(1, state.answered / safeTotal);
  const errorCount = state.wrong + state.timeouts;
  const playerAdvantage = Math.round(
    state.correct * 120
      - errorCount * 85
      - state.totalResponseMs / 500,
  );
  const botOne = Math.round(safeTotal * 90 * progressRatio);
  const botTwo = Math.round(safeTotal * 70 * progressRatio);
  const gaps: [number, number] = [playerAdvantage - botOne, playerAdvantage - botTwo];
  const place = (1 + gaps.filter((gap) => gap < 0).length) as 1 | 2 | 3;
  return { place, playerAdvantage, gaps };
}
